import React, { useMemo, useState } from 'react';
import { CalendarIcon, UsersIcon, UserIcon, BriefcaseIcon, TrendingUpIcon, DollarSignIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ClockIcon, BarChart3Icon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useData } from '../../contexts/DataContext';
import { formatCurrency, formatTime, getToday, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getStageName, getStageColor } from '../../utils/helpers';
import { usePermissions } from '../../hooks/usePermissions';
import { hasPermission } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
interface DashboardProps {
  onNavigate: (module: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const {
    patients,
    doctors,
    appointments,
    services,
    opportunities,
    contacts,
    getPatient,
    getDoctor,
    getService,
    getAppointmentsByDate,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount
  } = useData();
  const {
    user
  } = useAuth();
  const userRole = user?.role || '';
  const canViewStats = hasPermission(userRole, 'dashboard_stats', 'read');
  const canViewCalendar = hasPermission(userRole, 'dashboard_calendar', 'read');
  const canViewPipeline = hasPermission(userRole, 'dashboard_pipeline', 'read');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const today = getToday();
  const todayAppointments = getAppointmentsByDate(today);
  const selectedDateAppointments = getAppointmentsByDate(selectedDate);
  // KPIs
  const totalAppointments = appointments.length;
  const todayCount = getTodayAppointmentsCount();
  const activeDoctors = doctors.length;
  const totalServices = services.length;
  const totalContacts = contacts.length;
  const totalOpportunities = opportunities.length;
  // Appointments Statistics
  const appointmentStats = useMemo(() => {
    // Last 7 days trend
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayAppointments = appointments.filter(a => a.date === dateStr);
      last7Days.push({
        date: date.toLocaleDateString('es-ES', {
          weekday: 'short',
          day: 'numeric'
        }),
        total: dayAppointments.length,
        completadas: dayAppointments.filter(a => a.status === 'completada').length,
        programadas: dayAppointments.filter(a => a.status === 'programada').length,
        canceladas: dayAppointments.filter(a => a.status === 'cancelada').length
      });
    }
    // By Doctor
    const byDoctor = appointments.reduce((acc, apt) => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor?.name || 'Sin asignar';
      acc[doctorName] = (acc[doctorName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const doctorData = Object.entries(byDoctor).map(([name, count]) => ({
      name,
      citas: count
    })).sort((a, b) => b.citas - a.citas).slice(0, 5);
    return {
      last7Days,
      doctorData
    };
  }, [appointments, getDoctor]);
  // Pipeline stages
  const pipelineStages = [{
    id: 'contacto_inicial',
    name: 'Contacto Inicial'
  }, {
    id: 'evaluacion',
    name: 'Evaluación'
  }, {
    id: 'propuesta',
    name: 'Propuesta / Demo'
  }, {
    id: 'negociacion',
    name: 'Negociación'
  }, {
    id: 'completado',
    name: 'Cerrado'
  }];
  const getStageCount = (stage: string) => {
    return opportunities.filter(o => o.stage === stage).length;
  };
  const getStageAmount = (stage: string) => {
    return opportunities.filter(o => o.stage === stage).reduce((sum, o) => sum + o.amount, 0);
  };
  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }
  const getDateString = (day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => a.date === dateStr && a.status !== 'cancelada').length;
  };
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  // Recent appointments
  const recentAppointments = [...appointments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  return <div className="space-y-6" data-id="element-750">
      {/* Page Header */}
      <div className="flex items-center justify-between" data-id="element-751">
        <div data-id="element-752">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-753">
            Panel de Control
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-754">
            Resumen de actividad y métricas
          </p>
        </div>
        <div className="flex gap-3" data-id="element-755">
          {hasPermission(userRole, 'patients', 'full') && <Button variant="outline" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-757" />} onClick={() => onNavigate('patients')} data-id="element-756">
              Nuevo Paciente
            </Button>}
          {hasPermission(userRole, 'appointments', 'full') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-759" />} onClick={() => onNavigate('appointments')} data-id="element-758">
              Nueva Cita
            </Button>}
        </div>
      </div>

      {/* KPI Cards - Only if stats permission */}
      {canViewStats && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-760">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('appointments')} data-id="element-761">
            <div className="flex items-center gap-3" data-id="element-762">
              <div className="p-2 bg-sky-100 dark:bg-sky-900/30 rounded-lg" data-id="element-763">
                <CalendarIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" data-id="element-764" />
              </div>
              <div data-id="element-765">
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-766">
                  {totalAppointments}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-767">
                  Citas Totales
                </p>
              </div>
            </div>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('appointments')} data-id="element-768">
            <div className="flex items-center gap-3" data-id="element-769">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg" data-id="element-770">
                <ClockIcon className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-771" />
              </div>
              <div data-id="element-772">
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-773">
                  {todayCount}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-774">
                  Citas Hoy
                </p>
              </div>
            </div>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('doctors')} data-id="element-775">
            <div className="flex items-center gap-3" data-id="element-776">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-777">
                <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-778" />
              </div>
              <div data-id="element-779">
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-780">
                  {activeDoctors}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-781">
                  Doctores
                </p>
              </div>
            </div>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('services')} data-id="element-782">
            <div className="flex items-center gap-3" data-id="element-783">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg" data-id="element-784">
                <BriefcaseIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-785" />
              </div>
              <div data-id="element-786">
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-787">
                  {totalServices}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-788">
                  Servicios
                </p>
              </div>
            </div>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('contacts')} data-id="element-789">
            <div className="flex items-center gap-3" data-id="element-790">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg" data-id="element-791">
                <UsersIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" data-id="element-792" />
              </div>
              <div data-id="element-793">
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-794">
                  {totalContacts}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-795">
                  Contactos
                </p>
              </div>
            </div>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('opportunities')} data-id="element-796">
            <div className="flex items-center gap-3" data-id="element-797">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg" data-id="element-798">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-799" />
              </div>
              <div data-id="element-800">
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-801">
                  {totalOpportunities}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-802">
                  Oportunidades
                </p>
              </div>
            </div>
          </Card>
        </div>}

      {/* Appointments Statistics Charts - Only if stats permission */}
      {canViewStats && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-803">
          {/* 7 Days Trend */}
          <Card data-id="element-804">
            <CardHeader data-id="element-805">
              <CardTitle className="flex items-center gap-2" data-id="element-806">
                <TrendingUpIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" data-id="element-807" />
                Tendencia Últimos 7 Días
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-808">
              <ResponsiveContainer width="100%" height={300} data-id="element-809">
                <LineChart data={appointmentStats.last7Days} data-id="element-810">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-811" />
                  <XAxis dataKey="date" stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-812" />
                  <YAxis stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-813" />
                  <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-814" />
                  <Legend data-id="element-815" />
                  <Line type="monotone" dataKey="completadas" stroke="#10b981" strokeWidth={2} name="Completadas" dot={{
                fill: '#10b981',
                r: 4
              }} data-id="element-816" />
                  <Line type="monotone" dataKey="programadas" stroke="#3b82f6" strokeWidth={2} name="Programadas" dot={{
                fill: '#3b82f6',
                r: 4
              }} data-id="element-817" />
                  <Line type="monotone" dataKey="canceladas" stroke="#ef4444" strokeWidth={2} name="Canceladas" dot={{
                fill: '#ef4444',
                r: 4
              }} data-id="element-818" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Doctors by Appointments */}
          <Card data-id="element-819">
            <CardHeader data-id="element-820">
              <CardTitle className="flex items-center gap-2" data-id="element-821">
                <UserIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" data-id="element-822" />
                Top Doctores por Citas
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-823">
              <ResponsiveContainer width="100%" height={300} data-id="element-824">
                <BarChart data={appointmentStats.doctorData} data-id="element-825">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-826" />
                  <XAxis dataKey="name" stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-827" />
                  <YAxis stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-828" />
                  <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-829" />
                  <Bar dataKey="citas" fill="#3b82f6" radius={[8, 8, 0, 0]} name="Citas" data-id="element-830" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>}

      {/* Pipeline - Only if pipeline permission */}
      {canViewPipeline && <Card data-id="element-831">
          <CardHeader data-id="element-832">
            <CardTitle data-id="element-833">Pipeline de Ingresos</CardTitle>
          </CardHeader>
          <CardContent data-id="element-834">
            <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-835">
              {pipelineStages.map((stage, index) => {
            const count = getStageCount(stage.id);
            const amount = getStageAmount(stage.id);
            return <div key={stage.id} className="flex-1 min-w-[150px] p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-836">
                    <div className="flex items-center gap-2 mb-2" data-id="element-837">
                      <div className={`w-3 h-3 rounded-full ${getStageColor(stage.id)}`} data-id="element-838" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-839">
                        {stage.name}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-840">
                      {count}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-841">
                      {formatCurrency(amount)}
                    </p>
                  </div>;
          })}
            </div>
          </CardContent>
        </Card>}

      {/* Calendar and Appointments - Only if calendar permission */}
      {canViewCalendar && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-842">
          {/* Mini Calendar */}
          <Card data-id="element-843">
            <CardHeader data-id="element-844">
              <div className="flex items-center justify-between" data-id="element-845">
                <CardTitle data-id="element-846">Calendario</CardTitle>
                <div className="flex items-center gap-2" data-id="element-847">
                  <button onClick={prevMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" data-id="element-848">
                    <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-849" />
                  </button>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px] text-center" data-id="element-850">
                    {getMonthName(month)} {year}
                  </span>
                  <button onClick={nextMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" data-id="element-851">
                    <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-852" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-853">
              <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-854">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2" data-id="element-855">
                      {day}
                    </div>)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-id="element-856">
                {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className="aspect-square" data-id="element-857" />;
              }
              const dateStr = getDateString(day);
              const appointmentCount = getAppointmentCountForDay(day);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedDate;
              return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`
                      aspect-square rounded-lg text-sm relative
                      flex flex-col items-center justify-center
                      transition-colors
                      ${isToday ? 'bg-sky-500 text-white' : ''}
                      ${isSelected && !isToday ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400' : ''}
                      ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}
                    `} data-id="element-858">
                      <span data-id="element-859">{day}</span>
                      {appointmentCount > 0 && <span className={`
                        text-[10px] font-medium
                        ${isToday ? 'text-sky-100' : 'text-sky-500 dark:text-sky-400'}
                      `} data-id="element-860">
                          {appointmentCount} cita
                          {appointmentCount > 1 ? 's' : ''}
                        </span>}
                    </button>;
            })}
              </div>
            </CardContent>
          </Card>

          {/* Today's Appointments */}
          <Card data-id="element-861">
            <CardHeader data-id="element-862">
              <CardTitle data-id="element-863">
                Citas del {selectedDate === today ? 'Hoy' : selectedDate}
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-864">
              {selectedDateAppointments.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-865">
                  No hay citas programadas
                </p> : <div className="space-y-3" data-id="element-866">
                  {selectedDateAppointments.map(apt => {
              const patient = getPatient(apt.patientId);
              const doctor = getDoctor(apt.doctorId);
              const service = getService(apt.serviceId);
              return <div key={apt.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-867">
                        <div className="flex items-center gap-3" data-id="element-868">
                          <div className="text-center" data-id="element-869">
                            <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-870">
                              {formatTime(apt.time)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-871">
                              {apt.duration} min
                            </p>
                          </div>
                          <div data-id="element-872">
                            <p className="font-medium text-gray-900 dark:text-white" data-id="element-873">
                              {patient?.name || 'Paciente'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-874">
                              Dr. {doctor?.name} • {service?.name}
                            </p>
                          </div>
                        </div>
                        <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-875">
                          {getStatusName(apt.status)}
                        </Badge>
                      </div>;
            })}
                </div>}
            </CardContent>
          </Card>
        </div>}

      {/* Recent Appointments - Only if stats permission */}
      {canViewStats && <Card data-id="element-876">
          <CardHeader data-id="element-877">
            <div className="flex items-center justify-between" data-id="element-878">
              <CardTitle data-id="element-879">Citas Recientes</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => onNavigate('appointments')} data-id="element-880">
                Ver todas
              </Button>
            </div>
          </CardHeader>
          <CardContent data-id="element-881">
            <div className="overflow-x-auto" data-id="element-882">
              <table className="w-full" data-id="element-883">
                <thead data-id="element-884">
                  <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-885">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-886">
                      Paciente
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-887">
                      Doctor
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-888">
                      Servicio
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-889">
                      Fecha
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-890">
                      Hora
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-891">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-892">
                  {recentAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-893">
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-894">
                          {patient?.name || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-895">
                          Dr. {doctor?.name || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-896">
                          {service?.name || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-897">
                          {apt.date}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-898">
                          {formatTime(apt.time)}
                        </td>
                        <td className="py-3 px-4" data-id="element-899">
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-900">
                            {getStatusName(apt.status)}
                          </Badge>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>}

      {/* Show message if no sections are visible */}
      {!canViewStats && !canViewCalendar && !canViewPipeline && <Card data-id="element-901">
          <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-902">
            <BarChart3Icon className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-903" />
            <p className="text-lg font-medium" data-id="element-904">
              Bienvenido al Panel de Control
            </p>
            <p className="text-sm mt-1" data-id="element-905">
              No tienes acceso a las secciones avanzadas del panel. Contacta al
              administrador para solicitar permisos.
            </p>
          </div>
        </Card>}
    </div>;
}